import { Trans } from '@lingui/macro'

import * as React from 'react'

import { useFormContext, useFormState } from '~shared/segments/Form'
import { InputCheckbox } from '~ui/components/formElements/Checkbox/InputCheckbox'
import { Box, GridRow } from '~ui/components/layout/Box'
import { Text } from '~ui/components/typography/Text/Text'

import {
  usePersistedFormValuesCtx,
  useShouldRememberFormCtx,
} from '../contexts/FormPersistenceProvider'
import { TOrderForm } from '../contexts/FormProvider'
import { useRememberFormValues } from '../hooks/useRememberFormValues'

type TRememberFormValuesProps = FirstParam<typeof useRememberFormValues>

type TProps = TRememberFormValuesProps

// TODO some toast on setClearedStoredValues(true)

/**
 * An option to remember the contact part of the order.
 * It also runs the persisting logic (wrapped inside a hook).
 */
export const RememberForm: React.FC<TProps> = ({ persistForm, resetFormPersistence }) => {
  const { control } = useFormContext<TOrderForm>()
  const { isSubmitting } = useFormState({ control })

  const persistedForm = usePersistedFormValuesCtx()
  const resetForm = useRememberFormValues({ persistForm, resetFormPersistence }) // this hook handles the persistence update on input change
  const [clearedValues, setClearedValues] = React.useState(false)
  const { rememberForm, setRememberForm } = useShouldRememberFormCtx()

  const clearPersistedForm = () => {
    setClearedValues(true)
    resetForm()
  }

  const toggleRememberForm = () => setRememberForm(!rememberForm)
  const hasValuesToClear = !rememberForm && persistedForm && !clearedValues

  return (
    <GridRow gridTemplateColumns={`auto 1fr auto`} columnGap={`small`} alignItems={`center`}>
      <InputCheckbox
        value={`rememberForm`}
        isDisabled={isSubmitting}
        checkboxMarginTop={`0px`}
        onChange={toggleRememberForm}
        isChecked={Boolean(rememberForm)}
      />
      <Box cursor={`pointer`} onClick={toggleRememberForm} width={`fit-content`}>
        <Text fontSize={`s14`}>
          <Trans>remember form values</Trans>
        </Text>
      </Box>
      {hasValuesToClear && (
        <Box cursor={`pointer`} onClick={clearPersistedForm}>
          <Text fontSize={`s14`} fontStyle={`boldSemi`}>
            <Trans>clear stored values</Trans>
          </Text>
        </Box>
      )}
    </GridRow>
  )
}
